import { useAtomValue, useSetAtom } from 'jotai'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router'

import LoginCard from '../components/cards/LoginCard'
import useBar from '../components/common/Bar'
import UserProfile from '../components/UserProfile'
import { useUsers } from '../contexts/UserContext'
import { logoutCommandAtom } from '../domains/auth/AuthCommand'
import { authCleanupCommandAtom } from '../domains/auth/AuthCommand'
import { isAuthenticatedAtom } from '../domains/auth/AuthSelectors'
import { currentUserAtom } from '../domains/user/AtomUsers'
import UserService from '../services/UserService'

const ProfilePage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const logoutCommand = useSetAtom(logoutCommandAtom)
  const user = useAtomValue(currentUserAtom)
  const authCleanupCommand = useSetAtom(authCleanupCommandAtom)
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const { fetchUser } = useUsers()
  const bar = useBar()
  const [loading, setLoading] = useState(true)
  const [userData, setUserData] = useState(null)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const params = new URLSearchParams(window.location.search)

  const loadUserData = useCallback(
    async (forceRefresh = false) => {
      if (!isAuthenticated || !user) {
        return
      }

      if (!forceRefresh && userData) {
        setLoading(false)
        return
      }

      try {
        const userDetails = await fetchUser({ userId: user.resourceId, forceRefresh })
        setUserData(userDetails)
        setLoading(false)
      } catch (error) {
        if (error.status === 404 || error.message === 'User not found.') {
          bar.error('Something went wrong. Please try login again')
          await authCleanupCommand()

          navigate('/login')
          setLoading(false)
          return
        }
        bar.error('Failed to load profile: ' + error.message)
        setLoading(false)
      }
    },
    [fetchUser, navigate, userData, bar, user, isAuthenticated, authCleanupCommand],
  )

  const handleUpdateProfile = async (updatedData) => {
    try {
      const response = await UserService.updateUser(user.resourceId, updatedData)
      bar.success('Profile updated successfully')
      // Reload user data after successful update

      await loadUserData(true)

      // Check if there's a return URL to redirect back to from location state or search params (fallback)
      const returnUrl = location.state?.returnUrl || searchParams.get('return')
      if (returnUrl) {
        try {
          const decodedUrl = typeof returnUrl === 'string' ? decodeURIComponent(returnUrl) : returnUrl
          navigate(decodedUrl, { replace: true })
          return response
        } catch (error) {
          console.error('Invalid return URL:', error)
          // Continue with normal flow if return URL is invalid
        }
      }

      return response
    } catch (error) {
      console.error('Failed to update profile:', error)
      bar.error('Failed to update profile')
      throw error
    }
  }

  const handleCloseEditDialog = () => {
    const params = new URLSearchParams(searchParams)
    if (params.has('edit')) {
      params.delete('edit')
    }
    const newUrl = params.toString() ? `${location.pathname}?${params.toString()}` : location.pathname

    // 只改地址，不触发 React Router navigate
    window.history.replaceState(
      { ...(location.state || {}), openEdit: false }, // 保留/修改 state
      '',
      newUrl,
    )
  }

  useEffect(() => {
    // Only load user data when the component is mounted
    loadUserData()
  })

  // Show message from navigation state if available
  useEffect(() => {
    if (location.state?.message) {
      bar.info(location.state.message)
    }
  }, [location.state?.message, bar])

  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      await logoutCommand()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
      bar.error('Logout failed')
      setIsLoggingOut(false)
    }
  }

  if (!isAuthenticated) {
    return <LoginCard title="Sign in to View Profile" afterLogin={() => navigate('/profile')} />
  }

  if (!userData) {
    return null
  }

  const shouldOpenEdit = location.state?.openEdit || params.has('edit')

  return (
    <UserProfile
      user={userData}
      onLogout={handleLogout}
      onUpdateProfile={handleUpdateProfile}
      defaultOpenEdit={shouldOpenEdit}
      onCloseEdit={handleCloseEditDialog}
      isLoggingOut={isLoggingOut}
    />
  )
}

export default ProfilePage
